'use client';

import { Eye, EyeOff } from 'lucide-react';
import styles from './OwnerViewToggle.module.css';

/** Which lens the viewer is looking through. */
export type OwnerViewMode = 'owner' | 'public';

/**
 * Who is using the toggle. The mechanics are identical; only the wording differs:
 * the owner reads it as "me", an admin previewing someone else reads it as "the
 * owner". `owner` is the default so existing owner usage stays unchanged.
 */
export type OwnerViewAudience = 'owner' | 'admin';

interface OwnerViewToggleProps {
	value: OwnerViewMode;
	onChange: (mode: OwnerViewMode) => void;
	audience?: OwnerViewAudience;
}

type ToggleOption = { mode: OwnerViewMode; label: string; icon: typeof Eye; hint: string };

const OPTIONS_BY_AUDIENCE: Record<OwnerViewAudience, ToggleOption[]> = {
	owner: [
		{
			mode: 'owner',
			label: 'Como lo veo yo',
			icon: EyeOff,
			hint: 'Incluye los datos que mantienes ocultos del perfil público',
		},
		{
			mode: 'public',
			label: 'Como se ve públicamente',
			icon: Eye,
			hint: 'Solo lo que ve cualquier visitante',
		},
	],
	admin: [
		{
			mode: 'owner',
			label: 'Como lo ve el dueño',
			icon: EyeOff,
			hint: 'Incluye los datos que el dueño mantiene ocultos del perfil público',
		},
		{
			mode: 'public',
			label: 'Como se ve públicamente',
			icon: Eye,
			hint: 'Solo lo que ve cualquier visitante',
		},
	],
};

/**
 * Segmented control shown only to the profile's owner: both lenses stay visible
 * and clicking swaps between them.
 *
 * The active fill is a single thumb that slides between the two halves and
 * changes colour on the way (brand blue = mine, teal = public), so the switch
 * reads as one object moving rather than two lights blinking. `OPTIONS` order
 * defines the travel: index 0 sits left, index 1 is the translated state.
 */
export function OwnerViewToggle({ value, onChange, audience = 'owner' }: OwnerViewToggleProps) {
	const options = OPTIONS_BY_AUDIENCE[audience];
	return (
		<div className={styles.toggle} role="group" aria-label="Modo de vista del perfil">
			<span
				className={`${styles.thumb} ${value === 'public' ? styles.thumbPublic : ''}`}
				aria-hidden="true"
			/>
			{options.map(({ mode, label, icon: Icon, hint }) => {
				const active = value === mode;
				return (
					<button
						key={mode}
						type="button"
						onClick={() => onChange(mode)}
						className={`${styles.option} ${active ? styles.optionActive : ''}`}
						aria-pressed={active}
						title={hint}
					>
						<Icon size={12} className={styles.icon} />
						<span className={styles.optionLabel}>{label}</span>
					</button>
				);
			})}
		</div>
	);
}
